/**
 * PatternDiffView Component
 *
 * Side-by-side comparison of current vs regenerated intent patterns.
 * Shows what changed (added/removed/modified patterns) and allows
 * the operator to accept or reject the regenerated patterns.
 *
 * Validates: Requirements 5.4, 5.5, 5.7
 * - Requirement 5.4: WHEN generation completes, THE Console SHALL display
 *   the new patterns for operator review before saving.
 * - Requirement 5.5: THE Console SHALL allow the operator to accept or reject
 *   the regenerated patterns.
 * - Requirement 5.7: THE Console SHALL display a visual diff or comparison
 *   between current and regenerated patterns.
 */

import { useMemo } from 'react';
import { Button, Badge, Modal } from '../../design-system';
import { tokens } from '../../design-system/tokens';
import type { IntentConfig, IntentPatternDef } from './PatternEditor';

// ============================================
// TYPES
// ============================================

export interface PatternDiffViewProps {
  /** Whether the diff view is open */
  isOpen: boolean;
  /** Current intent configuration */
  currentConfig: IntentConfig;
  /** Newly generated intent configuration */
  generatedConfig: IntentConfig;
  /** Callback when operator accepts the new patterns */
  onAccept: () => void;
  /** Callback when operator rejects the new patterns */
  onReject: () => void;
  /** Whether the accept action is in progress */
  accepting?: boolean;
}

type DiffStatus = 'added' | 'removed' | 'modified' | 'unchanged';

interface IntentDiff {
  intentType: string;
  status: DiffStatus;
  currentDef?: IntentPatternDef;
  generatedDef?: IntentPatternDef;
  patternChanges?: PatternChange[];
}

interface PatternChange {
  pattern: string;
  status: 'added' | 'removed' | 'unchanged';
}

// ============================================
// HELPERS
// ============================================

function computeDiffs(
  current: IntentConfig,
  generated: IntentConfig
): IntentDiff[] {
  const diffs: IntentDiff[] = [];
  const allIntentTypes = new Set([
    ...Object.keys(current.patterns),
    ...Object.keys(generated.patterns),
  ]);

  for (const intentType of allIntentTypes) {
    const currentDef = current.patterns[intentType];
    const generatedDef = generated.patterns[intentType];

    if (!currentDef && generatedDef) {
      // New intent type added
      diffs.push({
        intentType,
        status: 'added',
        generatedDef,
        patternChanges: generatedDef.patterns.map((p) => ({ pattern: p, status: 'added' })),
      });
    } else if (currentDef && !generatedDef) {
      // Intent type removed
      diffs.push({
        intentType,
        status: 'removed',
        currentDef,
        patternChanges: currentDef.patterns.map((p) => ({ pattern: p, status: 'removed' })),
      });
    } else if (currentDef && generatedDef) {
      // Compare patterns
      const currentPatterns = new Set(currentDef.patterns);
      const generatedPatterns = new Set(generatedDef.patterns);

      const patternChanges: PatternChange[] = [];
      let hasChanges = false;

      // Patterns in generated but not in current (added)
      for (const p of generatedDef.patterns) {
        if (!currentPatterns.has(p)) {
          patternChanges.push({ pattern: p, status: 'added' });
          hasChanges = true;
        } else {
          patternChanges.push({ pattern: p, status: 'unchanged' });
        }
      }

      // Patterns in current but not in generated (removed)
      for (const p of currentDef.patterns) {
        if (!generatedPatterns.has(p)) {
          patternChanges.push({ pattern: p, status: 'removed' });
          hasChanges = true;
        }
      }

      // Check if priority or other fields changed
      if (
        currentDef.priority !== generatedDef.priority ||
        currentDef.confidenceBoost !== generatedDef.confidenceBoost ||
        currentDef.enabled !== generatedDef.enabled
      ) {
        hasChanges = true;
      }

      diffs.push({
        intentType,
        status: hasChanges ? 'modified' : 'unchanged',
        currentDef,
        generatedDef,
        patternChanges,
      });
    }
  }

  // Sort: modified/added/removed first, unchanged last
  const statusOrder: Record<DiffStatus, number> = {
    modified: 0,
    added: 1,
    removed: 2,
    unchanged: 3,
  };
  diffs.sort((a, b) => statusOrder[a.status] - statusOrder[b.status]);

  return diffs;
}

function getStatusBadgeVariant(status: DiffStatus): 'success' | 'error' | 'warning' | 'neutral' {
  switch (status) {
    case 'added':
      return 'success';
    case 'removed':
      return 'error';
    case 'modified':
      return 'warning';
    case 'unchanged':
      return 'neutral';
  }
}

function getStatusLabel(status: DiffStatus): string {
  switch (status) {
    case 'added':
      return 'Nuevo';
    case 'removed':
      return 'Eliminado';
    case 'modified':
      return 'Modificado';
    case 'unchanged':
      return 'Sin cambios';
  }
}

function getPatternColor(status: 'added' | 'removed' | 'unchanged'): string {
  switch (status) {
    case 'added':
      return tokens.colors.success.dark;
    case 'removed':
      return tokens.colors.error.dark;
    case 'unchanged':
      return tokens.colors.neutral[600];
  }
}

function getPatternBg(status: 'added' | 'removed' | 'unchanged'): string {
  switch (status) {
    case 'added':
      return tokens.colors.success.light;
    case 'removed':
      return tokens.colors.error.light;
    case 'unchanged':
      return 'transparent';
  }
}

// ============================================
// SUB-COMPONENTS
// ============================================

function IntentDiffSection({ diff }: { diff: IntentDiff }) {
  return (
    <div
      style={{
        marginBottom: tokens.spacing[4],
        padding: tokens.spacing[3],
        borderRadius: tokens.borderRadius.md,
        border: `1px solid ${tokens.colors.neutral[200]}`,
        backgroundColor: tokens.colors.neutral[50],
      }}
    >
      {/* Intent type header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: tokens.spacing[2],
        }}
      >
        <span
          style={{
            fontFamily: tokens.typography.fontFamily.mono,
            fontSize: tokens.typography.fontSize.sm,
            fontWeight: tokens.typography.fontWeight.semibold,
            color: tokens.colors.neutral[800],
          }}
        >
          {diff.intentType}
        </span>
        <Badge variant={getStatusBadgeVariant(diff.status)} size="sm">
          {getStatusLabel(diff.status)}
        </Badge>
      </div>

      {/* Priority/boost changes */}
      {diff.status === 'modified' && diff.currentDef && diff.generatedDef && (
        <div
          style={{
            display: 'flex',
            gap: tokens.spacing[4],
            marginBottom: tokens.spacing[2],
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.neutral[500],
          }}
        >
          {diff.currentDef.priority !== diff.generatedDef.priority && (
            <span>
              Prioridad: {diff.currentDef.priority} → {diff.generatedDef.priority}
            </span>
          )}
          {diff.currentDef.confidenceBoost !== diff.generatedDef.confidenceBoost && (
            <span>
              Boost: {diff.currentDef.confidenceBoost} → {diff.generatedDef.confidenceBoost}
            </span>
          )}
        </div>
      )}

      {/* Pattern changes */}
      {diff.patternChanges && diff.patternChanges.length > 0 && (
        <div style={{ marginTop: tokens.spacing[2] }}>
          {diff.patternChanges.map((change, idx) => (
            <div
              key={`${change.pattern}-${idx}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: tokens.spacing[2],
                padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                marginBottom: tokens.spacing[1],
                borderRadius: tokens.borderRadius.sm,
                backgroundColor: getPatternBg(change.status),
              }}
            >
              <span
                style={{
                  fontSize: tokens.typography.fontSize.sm,
                  fontWeight: tokens.typography.fontWeight.bold,
                  color: getPatternColor(change.status),
                  width: '16px',
                  textAlign: 'center',
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                {change.status === 'added' ? '+' : change.status === 'removed' ? '−' : ' '}
              </span>
              <code
                style={{
                  fontSize: tokens.typography.fontSize.xs,
                  fontFamily: tokens.typography.fontFamily.mono,
                  color: getPatternColor(change.status),
                  wordBreak: 'break-all',
                }}
              >
                {change.pattern}
              </code>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * PatternDiffView — Side-by-side comparison of current vs regenerated patterns.
 *
 * Displays in a modal showing what changed between the current and newly
 * generated patterns, with accept/reject actions.
 *
 * @example
 * ```tsx
 * <PatternDiffView
 *   isOpen={showDiff}
 *   currentConfig={currentConfig}
 *   generatedConfig={newConfig}
 *   onAccept={handleAccept}
 *   onReject={handleReject}
 * />
 * ```
 */
export function PatternDiffView({
  isOpen,
  currentConfig,
  generatedConfig,
  onAccept,
  onReject,
  accepting,
}: PatternDiffViewProps) {
  const diffs = useMemo(
    () => computeDiffs(currentConfig, generatedConfig),
    [currentConfig, generatedConfig]
  );

  const changesSummary = useMemo(() => {
    const added = diffs.filter((d) => d.status === 'added').length;
    const removed = diffs.filter((d) => d.status === 'removed').length;
    const modified = diffs.filter((d) => d.status === 'modified').length;
    const unchanged = diffs.filter((d) => d.status === 'unchanged').length;
    return { added, removed, modified, unchanged };
  }, [diffs]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onReject}
      title="Patrones Regenerados"
      size="lg"
      closeOnOverlayClick={false}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div
            style={{
              fontSize: tokens.typography.fontSize.xs,
              color: tokens.colors.neutral[500],
            }}
          >
            {changesSummary.modified > 0 && (
              <span style={{ marginRight: tokens.spacing[3] }}>
                {changesSummary.modified} modificado{changesSummary.modified > 1 ? 's' : ''}
              </span>
            )}
            {changesSummary.added > 0 && (
              <span style={{ marginRight: tokens.spacing[3] }}>
                {changesSummary.added} nuevo{changesSummary.added > 1 ? 's' : ''}
              </span>
            )}
            {changesSummary.removed > 0 && (
              <span>
                {changesSummary.removed} eliminado{changesSummary.removed > 1 ? 's' : ''}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: tokens.spacing[3] }}>
            <Button
              variant="ghost"
              onClick={onReject}
              disabled={accepting}
              aria-label="Rechazar patrones regenerados"
            >
              Rechazar
            </Button>
            <Button
              variant="primary"
              onClick={onAccept}
              loading={accepting}
              aria-label="Aceptar patrones regenerados"
            >
              Aceptar
            </Button>
          </div>
        </div>
      }
    >
      <div>
        {/* Summary */}
        <p
          style={{
            margin: 0,
            marginBottom: tokens.spacing[4],
            fontSize: tokens.typography.fontSize.sm,
            color: tokens.colors.neutral[600],
            lineHeight: tokens.typography.lineHeight.normal,
          }}
        >
          Revise los cambios en los patrones de intent antes de aceptarlos. Los patrones aceptados
          reemplazarán la configuración actual.
        </p>

        {/* Diff list */}
        <div
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            paddingRight: tokens.spacing[2],
          }}
        >
          {diffs.map((diff) => (
            <IntentDiffSection key={diff.intentType} diff={diff} />
          ))}
        </div>
      </div>
    </Modal>
  );
}

export default PatternDiffView;
